<script setup lang="ts">
// TODO: сделать свой компонент после миграции на vue3 (нужен <Teleport>)
import { Dropdown as FloatingVueDropdown, options as floatingVueOptions } from 'floating-vue';
import { IconChevronDown, IconLoader } from '../../../icons';
import LabelError from '../LabelError.vue';
import LabelBase from '../LabelBase.vue';

import 'floating-vue/dist/style.css';

interface IDropdownOption {
  value: string | number;
  isDisabled?: boolean;
}

floatingVueOptions.themes['dropdown-mm'] = {
  $extend: 'dropdown',
  $resetCss: true,
};

const props = withDefaults(
  defineProps<{
    options?: IDropdownOption[];
    modelValue?: IDropdownOption;
    error?: string;
    label?: string;
    isRequired?: boolean;
    emptyMessage?: string;
    noAutoFocus?: boolean;
    isLoading?: boolean;
    isHideAfterSelectEnabled?: boolean;
    isEmptyOptionsShown?: boolean;
    disabled?: boolean;
    container?: any;
  }>(),
  {
    options: () => [],
    emptyMessage: '(пусто)',
    isHideAfterSelectEnabled: true,
    isEmptyOptionsShown: true,
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: IDropdownOption, hideFn: () => void): void;
  (e: 'buttonClick'): void;
  (e: 'hide'): void;
  (e: 'show'): void;
}>();

function onSelect(option: IDropdownOption, hideFn: () => void) {
  emit('update:modelValue', option, hideFn);
  if (props.isHideAfterSelectEnabled) hideFn?.();
}

const isPopperShown = computed(() => props.isEmptyOptionsShown || props.options.length);
</script>

<template>
  <div class="dropdown__wrapper">
    <LabelBase :label="label" :is-required="isRequired" />

    <FloatingVueDropdown
      :disabled="disabled"
      theme="dropdown-mm"
      auto-size
      :distance="4"
      :no-auto-focus="noAutoFocus"
      :container="container"
      @hide="$emit('hide')"
      @show="$emit('show')"
    >
      <template #default="{ shown, show, hide }">
        <div
          class="dropdown__button"
          :class="{ dropdown__button_error: error && !shown, dropdown__button_focused: shown }"
          @click="$emit('buttonClick')"
        >
          <slot name="button" :show="show" :hide="hide" />
          <IconLoader v-if="isLoading" class="dropdown__button__loader" />
          <IconChevronDown
            v-else
            class="dropdown__button__chevron"
            :class="{ dropdown__button__chevron_rotated: shown }"
          />

          <LabelError v-if="!shown" class="dropdown__button__error-label" :error-text="error" />
        </div>
      </template>

      <template #popper="{ hide }">
        <div v-if="isPopperShown" class="dropdown__popper">
          <slot name="options-prepend" />
          <div class="dropdown__options">
            <div
              v-for="option in options"
              :key="option.value"
              class="dropdown__options__option"
              :class="{ dropdown__options__option_disabled: option.isDisabled }"
              @click="onSelect(option, hide)"
            >
              <slot name="option" :hide="hide" :option="option">{{ option.value }}</slot>
            </div>

            <div
              v-if="!options?.length"
              class="dropdown__options__option dropdown__options__option_empty"
            >
              {{ emptyMessage }}
            </div>
          </div>
        </div>
      </template>
    </FloatingVueDropdown>
  </div>
</template>

<style lang="stylus" scoped>
.dropdown
  &__wrapper
    display flex
    flex-direction column
    position relative
    gap 8px
    width 100%

  &__button
    display grid
    grid-template-columns 1fr 20px
    align-items center
    border-radius 12px
    padding 0 12px 0 16px
    gap 6px
    height 40px
    background-color #FFFFFF
    border solid 1px var(--border-regular)
    cursor pointer
    color var(--text-primary)
    &:hover
      border solid 1px var(--border-focused)
    &_focused
      border solid 1px var(--border-focused)
    &_error
      border solid 1px var(--border-negative)!important

    &__loader
      margin-left auto
      font-size 20px

    &__chevron
      margin-left auto
      font-size 20px
      transition-duration .3s
      &_rotated
        transform rotateX(180deg)

    &__error-label
      position absolute
      bottom -18px
      left 0
      right 0

  &__popper
    display flex
    flex-direction column
    border-radius 12px
    background-color var(--white)
    box-shadow var(--box-shadow-soft-small)
    overflow-y auto
    max-height 260px

    &::-webkit-scrollbar
      width 10px
      border-radius 8px

    &::-webkit-scrollbar-thumb
      border-radius 8px
      background-color #EDEFF2

    &::-webkit-scrollbar-track
      border-radius 0 12px 12px 0
      background-color rgba(1,1,1, 0.02)

  &__options
    display flex
    flex-direction column

    &__option
      padding 12px 16px
      font-size 14px
      font-weight 400
      cursor pointer
      position relative
      white-space nowrap
      overflow hidden
      text-overflow: ellipsis
      &_disabled
        pointer-events none
      +Media(Laptop, Desktop, DesktopMax)
        &:hover
          background-color var(--surface-primary-hovered)


      //divider
      &:nth-of-type(n+2)::before
        content ''
        position absolute
        top 0
        right 0
        left 16px
        height 0.5px
        background-color var(--border-strong)
      &_empty
        color var(--text-secondary)
        pointer-events none
</style>

<style lang="stylus">
.v-popper--theme-dropdown-mm.v-popper__popper
    z-index unset

.v-popper--theme-dropdown-mm
  background-color transparent
  border-radius 12px

  .v-popper__wrapper
    background-color transparent
    border-radius 12px

  .v-popper__inner
    background-color transparent
    border-radius 12px
    overflow visible

    & > div
      background-color transparent
      border-radius 12px

  .v-popper__arrow-container
    display none

  .v-popper__backdrop
    border-radius 12px
    background-color transparent
    display none
</style>
